/* eslint-disable max-lines */
import { createMuiTheme } from '@material-ui/core';
import {
  BorderRadiusPalette,
  BreakpointsPalette,
  ColorPalette,
  FontSizePalette,
  FontWeightPalette,
  StatusColors,
  ZIndexPalette,
} from '../models/style';
const withOpacity = (color?: string, opacity?: number): string => {
  if (!color || !opacity) return color || '';
  const hex = color.replace('#', '');
  if (opacity < 0 || opacity > 1) {
    console.warn(`Opacity must be between 0 and 1, received: ${opacity}`);
    opacity = Math.max(0, Math.min(1, opacity));
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
const withShade = (color?: string, shade?: number): string => {
  if (!color || shade === undefined) return color || '';
  const hex = color.replace('#', '');

  // Clamp shade between 0 and 2 (0 = black, 1 = original, 2 = white)
  shade = Math.max(0, Math.min(2, shade));

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  let newR;
  let newG;
  let newB;

  if (shade < 1) {
    // Darker shade (towards black)
    const factor = shade;
    newR = Math.round(r * factor);
    newG = Math.round(g * factor);
    newB = Math.round(b * factor);
  } else {
    // Lighter shade (towards white)
    const factor = shade - 1;
    newR = Math.round(r + (255 - r) * factor);
    newG = Math.round(g + (255 - g) * factor);
    newB = Math.round(b + (255 - b) * factor);
  }

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

const graphicsColors = {
  lightBlue: 'hsl(220, 100%, 87%)',
  green: 'hsl(141, 43%, 82%)',
  lightPink: 'hsl(340, 80%, 88%)',
  purple: 'hsl(253, 65%, 83%)',
  blue: 'hsl(220, 25%, 71%)',
  pink: 'hsla(348, 100%, 88%, 1.00)',
  yellow: 'hsl(54, 100%, 95%)',
  darkBlue: 'hsl(225, 14%, 52%)',
  lightPurple: 'hsl(256, 100%, 91%)',
  orange: 'hsl(22, 100%, 86%)',
  red: 'hsl(0, 86%, 83%)',
};
// Light theme: Uses grey9 (#4D4D4D) for primary text
// Dark theme: Uses grey10 (#e1e1e2) for primary text
// Placeholders: grey7 for both light and dark themes
// Disabled text: black (dark) / grey7 (light)
export const colorPalette: ColorPalette = {
  withOpacity,
  withShade,
  isDarkMode: false,
  black: '#141414',
  white: '#FFFFFF',
  grey: {
    grey1: '#F3F3F3', // for background disabled
    grey2: '#EDEDED',
    grey3: '#E5E5E5',
    grey4: '#D6D6D6',
    grey5: '#C5C5C5', // for border and text disabled
    grey6: '#A6A6A6',
    grey7: '#919191',
    grey8: '#6A6A6A',
    grey9: '#4D4D4D', // for text color
    grey10: '#2C2C2C', // grey-10 for background modals with opacity 0.4 => #ababab/  #2c2c2c66
  },
  red: '#E61B34',
  orange: '#F7A513',
  yellow: '#FFD000',
  green: '#259F0C',

  turquoise: {
    background: '#F1F8F8',
    dark15: '#deeff1',
    light: '#CCF7F7',
    accent2: '#cceaed',
    softTurquoise: '#92C6CC',
    dark: '#319AA6',
    accent: '#3E7278',
    normal: '#247079',
    gradient: 'linear-gradient(135deg, #319AA6 0%, #63CEDA 100%)',
  },
  graphics: graphicsColors,
  iconColors: {
    redAlert: '#E85D04',
    lessCritical: '#247079',
    greenCheck: '#319AA6',
    expired: '#E61B34',
    critical: '#DD9412',
  },
};

export const colorPaletteDark: ColorPalette = {
  ...colorPalette,
  withOpacity,
  withShade,
  black: '#FFFFFF', // white-1
  white: '#303234', // withOpacity('#FFFFFF', 0.1), white-3 with opacity 10%
  grey: {
    grey1: '#1c1c1c', // grey1
    grey2: '#252628', // grey-2 background1
    grey3: '#2F3032', // grey-3 backgrounds 2
    grey4: '#3C3D3F', // grey-4 Border colors input color
    grey5: '#4D4D4D', // grey-5 // Disabled states #4D4D4D
    grey7: '#8c8d8e', // grey-6 Low emphasis text
    grey8: '#C5C5C5', // grey-7
    grey9: '#E3E3E3', // grey 8 hover states with opacity 0.30 and selected 0.15
    grey10: '#e1e1e2', // withOpacity('#FFFFFF', 0.87), // white-2 with opacity 87% Primary text
    grey6: '',
  },
  red: '#EB5545',
  yellow: '#FFD426',
  orange: '#F7A513',
  green: '#6BA772', // medium green
  turquoise: {
    accent2: '#daf1f0', // withOpacity('#83D1CE', 0.3), 2d accent
    light: '#a8d5d9', // withOpacity('#2695A1', 0.4), // light green
    accent: '#83D1CE', // accent
    accent3: '#83D1CE1A', // accent con 10%
    normal: '#2695A1', // medium green
    dark: '#295E62', // darker green
    softTurquoise: '#2d4448', // disabled buttons
    dark15: '#475355',
  },
  graphics: graphicsColors,

  iconColors: {
    redAlert: '#FF7116',
    lessCritical: '#83D1CE',
    greenCheck: '#83D1CE',
    expired: '#EB5545',
    critical: '#FFD426',
  },

  isDarkMode: true,
};

export const statusColors: StatusColors = {
  error: '#E61B34',
  warning: '#FF9400',
  success: '#7ED321',
};

export const statusColorsDark: StatusColors = {
  error: '#EB5545',
  warning: '#FFD426',
  success: '#6BA772',
};

export const fontSizePalette: FontSizePalette = {
  xxSmall: '0.6rem', // 10px
  xSmall: '0.75rem', // 12px
  small: '0.9rem', // 14px
  medium: '1rem', // 16px
  large: '1.125rem', // 18px
  xLarge: '1.5rem', // 24px
  xlLarge: '1.75rem', // 28px
  xxLarge: '2rem', // 32px
  xxxLarge: '2.25rem', // 36px
};

export const fontWeightPalette: FontWeightPalette = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
};

export const fontFamily = 'Avenir, Roboto, sans-serif';

export const opacity = {
  medium: 0.25,
};

export const boxShadowPalette = {
  medium: '0px 2px 10px 0px #64646440',
};

export const zIndexPalette: ZIndexPalette = {
  lowest: -1,
  low: 1,
  medium: 10,
  high: 50,
  highest: 100,
};
export const headerHeight = 70;
export const blueBarHeight = 50;
export const actionBarHeight = 43;
export const toogleHeight = 50;

// adding reponsive breakpoints
export const breakpointsPalette: BreakpointsPalette = {
  s: '1024px',
  m: '1224px',
  l: '1380px',
};

export const turquoiseGradient = `linear-gradient(135deg, ${colorPalette.turquoise.normal} 0%, ${colorPalette.turquoise.background} 100%)`;

export const spacingFunction = (value: number): string => {
  return `${value * 0.25}rem`;
};
export const pxToRem = (px: number): string => {
  // @note always use odd numbers
  return `${px / 16}rem`;
};

export const sizePalette = {
  // Spacing (padding, margin)
  spacing: {
    custom: spacingFunction,
    pxToRem,
    0: '0px',
    px: '1px',
    0.5: spacingFunction(0.5), // 0.125rem (2px)
    1: spacingFunction(1), // 0.25rem (4px)
    1.5: spacingFunction(1.5), // 0.375rem (6px)
    2: spacingFunction(2), // 0.5rem (8px)
    2.5: spacingFunction(2.5), // 0.625rem (10px)
    3: spacingFunction(3), // 0.75rem (12px)
    4: spacingFunction(4), // 1rem (16px)
    5: spacingFunction(5), // 1.25rem (20px)
    6: spacingFunction(6), // 1.5rem (24px)
    8: spacingFunction(8), // 2rem (32px)
    12: spacingFunction(12), // 3rem (48px)
    16: spacingFunction(16), // 4rem (64px)
    24: spacingFunction(24), // 6rem (96px)
    32: spacingFunction(32), // 8rem (128px)
    48: spacingFunction(48), // 12rem (192px)
    64: spacingFunction(64), // 16rem (256px)
    96: spacingFunction(96), // 24rem (384px)
    128: spacingFunction(128), // 32rem (512px)
    160: spacingFunction(160), // 40rem (640px)
    192: spacingFunction(192), // 48rem (768px)
    224: spacingFunction(224), // 56rem (896px)
    256: spacingFunction(256), // 64rem (1024px)
  },
  // Width/Height
  size: {
    auto: 'auto',
    full: '100%',
    screen: '100vw',
    fit: 'fit-content',
    min: 'min-content',
    max: 'max-content',
    // Frazioni
    '1/2': '50%',
    '1/3': '33.333333%',
    '2/3': '66.666667%',
    '1/4': '25%',
    '2/4': '50%',
    '3/4': '75%',
    '1/5': '20%',
    '2/5': '40%',
    '3/5': '60%',
    '4/5': '80%',
    '1/6': '16.666667%',
    '5/6': '83.333333%',
    '1/12': '8.333333%',
    '2/12': '16.666667%',
    '3/12': '25%',
    '4/12': '33.333333%',
    '5/12': '41.666667%',
    '6/12': '50%',
    '7/12': '58.333333%',
    '8/12': '66.666667%',
    '9/12': '75%',
    '10/12': '83.333333%',
    '11/12': '91.666667%',
  },
  // Line height
  lineHeight: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
    3: '.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    7: '1.75rem', // 28px
    8: '2rem', // 32px
    9: '2.25rem', // 36px
    10: '2.5rem', // 40px
  },
  // Letter spacing
  letterSpacing: {
    tight: '-0.04rem',
    normal: '-0.003rem',
    wide: '0.16rem',
    widest: '0.056rem',
  },
  // Box shadow
  boxShadow: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    DEFAULT: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
    none: 'none',
  },
  // Opacity
  opacity: {
    0: '0',
    5: '0.05',
    10: '0.1',
    20: '0.2',
    25: '0.25',
    30: '0.3',
    40: '0.4',
    50: '0.5',
    60: '0.6',
    70: '0.7',
    75: '0.75',
    80: '0.8',
    90: '0.9',
    95: '0.95',
    100: '1',
  },
  // Transition duration
  transitionDuration: {
    75: '75ms',
    100: '100ms',
    150: '150ms',
    200: '200ms',
    300: '300ms',
    500: '500ms',
    700: '700ms',
    1000: '1000ms',
  },
  // Transition timing function
  transitionTimingFunction: {
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    'in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  borderRadius: {
    none: '0px',
    sm: '0.125rem', // 2px
    DEFAULT: '0.1875rem', // 3px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
    '2xl': '1rem', // 16px
    '3xl': '1.5rem', // 24px
    '4xl': '2rem', // 32px
    full: '9999px',
  },
  // Border width
  borderWidth: {
    0: '0px',
    DEFAULT: '1px',
    2: '2px',
    4: '4px',
    8: '8px',
  },
};

export const muiLightTheme = createMuiTheme({
  palette: {
    type: 'light',
    primary: {
      main: colorPalette.turquoise.normal,
      dark: colorPalette.turquoise.dark,
      light: colorPalette.turquoise.light,
    },
    secondary: {
      main: colorPalette.grey.grey7,
      light: colorPalette.grey.grey5,
      dark: colorPalette.grey.grey9,
    },
    background: {
      default: colorPalette.grey.grey2,
      paper: colorPalette.white,
    },
    text: {
      primary: colorPalette.grey.grey9,
      secondary: colorPalette.grey.grey7,
    },
    error: {
      main: statusColors.error,
    },
    warning: {
      main: statusColors.warning,
    },
    success: {
      main: statusColors.success,
    },
    grey: {
      50: colorPalette.grey.grey1,
      100: colorPalette.grey.grey2,
      200: colorPalette.grey.grey3,
      300: colorPalette.grey.grey4,
      400: colorPalette.grey.grey5,
      500: colorPalette.grey.grey6,
      600: colorPalette.grey.grey7,
      700: colorPalette.grey.grey8,
      800: colorPalette.grey.grey9,
      900: colorPalette.grey.grey10,
    },
  },
});

export const muiDarkTheme = createMuiTheme({
  palette: {
    type: 'dark',
    primary: {
      main: colorPaletteDark.turquoise.normal,
      dark: colorPaletteDark.turquoise.dark,
      light: colorPaletteDark.turquoise.light,
    },
    secondary: {
      main: colorPaletteDark.grey.grey7,
      light: colorPaletteDark.grey.grey5,
      dark: colorPaletteDark.grey.grey9,
    },
    background: {
      default: colorPaletteDark.grey.grey2,
      paper: colorPaletteDark.grey.grey3,
    },
    text: {
      primary: colorPaletteDark.grey.grey10,
      secondary: colorPaletteDark.grey.grey7,
    },
    error: {
      main: statusColorsDark.error,
    },
    warning: {
      main: statusColorsDark.warning,
    },
    success: {
      main: statusColorsDark.success,
    },
    grey: {
      50: colorPaletteDark.grey.grey1,
      100: colorPaletteDark.grey.grey2,
      200: colorPaletteDark.grey.grey3,
      300: colorPaletteDark.grey.grey4,
      400: colorPaletteDark.grey.grey5,
      500: colorPaletteDark.grey.grey6,
      600: colorPaletteDark.grey.grey7,
      700: colorPaletteDark.grey.grey8,
      800: colorPaletteDark.grey.grey9,
      900: colorPaletteDark.grey.grey10,
    },
  },
});

export const borderRadiusPalette: BorderRadiusPalette = {
  input: '3px',
  dropDown: {
    small: '9px',
    normal: '3px',
  },
  textArea: '3px',
  searchInput: {
    small: '12px',
    normal: '25px',
  },
  numberInput: '3px',
  multiSelect: '3px',
};
